import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { CropMarks } from "@/components/common/CropMarks";
import { Reveal } from "@/components/common/Reveal";
import { useBackground } from "@/contexts/BackgroundContext";
import { cn } from "@/lib/cn";

const LAYERS = [
  {
    label: "Agent Layer",
    borderClass: "border-l-purple-500",
    tags: ["XENI", "Robolab X", "Metaroom"],
  },
  {
    label: "Governance Layer",
    borderClass: "border-l-teal-500",
    tags: ["Compliance gates", "Audit trails", "Human oversight"],
  },
  {
    label: "Infrastructure Layer",
    borderClass: "border-l-[var(--color-fg-tertiary)]",
    tags: ["Encrypt", "Store", "Log", "Meter", "Deliver"],
  },
];

function EndpointLabel({ children }: { children: string }) {
  return (
    <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-widest">
      {children}
    </span>
  );
}

function ConnectorLine() {
  return (
    <div
      className="w-px mx-auto"
      style={{
        height: "32px",
        backgroundColor: "var(--color-border)",
      }}
    />
  );
}

export function ArchitectureDiagram() {
  const containerRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const { overlayOpacity } = useBackground();

  useEffect(() => {
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  const overlayColor = `rgba(var(--color-overlay-rgb),${overlayOpacity})`;

  return (
    <section
      id="architecture"
      className="relative z-10 backdrop-blur-[2px] py-24 px-8"
      style={{
        background: overlayColor,
        transition: "background 1.5s ease-in-out",
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-32 -translate-y-full pointer-events-none"
        style={{
          background: `linear-gradient(to bottom, transparent, ${overlayColor})`,
          transition: "background 1.5s ease-in-out",
        }}
        aria-hidden="true"
      />
      <CropMarks />

      <div className="max-w-6xl mx-auto w-full">
        <Reveal>
          <div ref={containerRef} className="grid grid-cols-1 md:grid-cols-2 md:items-center gap-12">
            {/* Left column — title + explanatory text */}
            <div>
              <h2 className="text-3xl md:text-5xl font-light tracking-tight mb-4 font-[family-name:var(--font-display)]">
                Architecture
              </h2>
              <p className="text-lg text-[var(--color-fg-secondary)] max-w-xl font-light mb-8">
                How a request flows through the COSX platform. From client input to auditable output.
              </p>
              <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed mb-4">
                Every COSX agent follows the same path. A client request enters at the top, passes through
                the governance layer for compliance checks and audit logging, then reaches the shared
                infrastructure that encrypts, stores, and delivers the response.
              </p>
              <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed">
                This architecture means every new agent launched on the platform inherits enterprise-grade
                security, billing, data residency, and compliance on day one. No per-product integration
                work. No gaps.
              </p>
            </div>

            {/* Right column — vertical flow diagram */}
            <div className="flex flex-col items-center relative" ref={flowRef}>
              {/* Traveling dot */}
              {isVisible && !prefersReducedMotion && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 z-10 pointer-events-none"
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: "var(--color-fg-tertiary)",
                    animation: "travelDot 6s ease-in-out infinite",
                  }}
                />
              )}

              <EndpointLabel>Client Request</EndpointLabel>
              <ConnectorLine />

              {LAYERS.map((layer, i) => (
                <div
                  key={layer.label}
                  className="w-full flex flex-col items-center"
                  style={{
                    transform: isVisible ? "none" : "translateY(16px)",
                    opacity: isVisible ? 1 : 0,
                    transition: prefersReducedMotion
                      ? "none"
                      : `opacity 0.5s ease-out ${i * 120}ms, transform 0.5s ease-out ${i * 120}ms`,
                  }}
                >
                  <div
                    className={cn(
                      "border border-[var(--color-border)] bg-[var(--color-surface)] w-full max-w-sm overflow-hidden",
                      layer.borderClass,
                      "border-l-4",
                    )}
                  >
                    <div className="px-4 py-3">
                      <span className="text-sm font-[family-name:var(--font-mono)] font-medium text-[var(--color-fg)]">
                        {layer.label}
                      </span>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {layer.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] px-1.5 py-0.5 border border-[var(--color-border)]"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <ConnectorLine />
                </div>
              ))}

              <EndpointLabel>Auditable Output</EndpointLabel>

              <style>{`
                @keyframes travelDot {
                  0% { top: 0%; opacity: 0; }
                  3% { opacity: 1; }
                  12% { top: 12%; }
                  15%, 28% { top: 15%; }
                  38% { top: 42%; }
                  40%, 53% { top: 45%; }
                  63% { top: 72%; }
                  65%, 78% { top: 75%; }
                  88% { top: 95%; }
                  95% { top: 98%; opacity: 1; }
                  100% { top: 100%; opacity: 0; }
                }
              `}</style>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
